import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import format from 'string-template';
import { EditOutlined } from '@mui/icons-material';
import * as Sentry from '@sentry/browser';

import Button, { LinkButton, LoadingButton, variants } from '../../../../../components/Button';
import ImageCrop from '../../../../../components/ImageCrop';
import { getCroppedImageUrl } from '../../../../../components/ImageCrop/utils';
import InputLabel from '../../../../../components/v2/InputLabel';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useStorage, { StorageProcessState } from '../../../../../hooks/useStorage';
import { CONVERTED_UPLOAD_FORMAT, toDisplayableFile } from '../../../../../utils/dataProcessing';
import { getFileName } from '../../../../../utils/file';
import { pathPlaceholder, storagePaths } from '../../../../../utils/firebasePaths';
import useToast from '../../../../hooks/useToast';
import { LeadConfigOptions } from '../../../../Model/CoachConfig';
import {
  HeroImageContainer,
  HeroImageDescription,
  HeroImageDescriptionContainer,
  HeroImageTipsDescription,
  HeroImageUploader,
  StyledEditIcon,
  StyledFileSelectButton,
  StyledImageSearch,
} from '../../../Config/components/CheckoutPageConfiguration/HeroImageSetting/styles';

import { ContactPhotoPreview, ContactPhotoWrapper } from './styles';
import texts from './texts.json';

const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_UPLOAD_SIZE = MAX_FILE_SIZE_MB * 1000000;
const PHOTO_SIZE_PX = 512;
const PHOTO_QUALITY = 0.9;

/**
 * The photo saved with the contact IS leads add from the end screen, cropped square and shrunk.
 * Saved as soon as it is uploaded; empty means the coach's hero image.
 */
const ContactPhotoSetting = ({
  coachConfigDoc,
  coachAvatarUrl,
  userName,
  isReadOnly,
  onCropOpenChange,
}) => {
  const customPhotoUrl = coachConfigDoc.contactCardPhotoUrl;
  const savedPhotoUrl = customPhotoUrl || coachAvatarUrl;
  const [fileToUpload, setFileToUpload] = useState(null);
  const [pickedPhotoUrl, setPickedPhotoUrl] = useState('');
  const previewUrl = pickedPhotoUrl || savedPhotoUrl;
  const [cropper, setCropper] = useState();
  const [isUploading, setIsUploading] = useState(false);

  const { uploadAttachment } = useStorage();
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  useEffect(() => () => URL.revokeObjectURL(pickedPhotoUrl), [pickedPhotoUrl]);

  useEffect(() => onCropOpenChange(!!fileToUpload), [fileToUpload, onCropOpenChange]);

  const onImageSelected = useCallback(async ({ target }) => {
    const [file] = target.files;
    if (!file) {
      return;
    }
    if (file.size >= MAX_FILE_UPLOAD_SIZE) {
      showToast(format(texts.errors.fileSize, { maxFileSize: MAX_FILE_SIZE_MB }), { error: true });
      return;
    }
    try {
      const croppableFile = await toDisplayableFile(file);
      setPickedPhotoUrl(URL.createObjectURL(croppableFile));
      setFileToUpload(croppableFile);
    } catch (error) {
      Sentry.captureException(error, { extra: { message: 'Failed to read a contact photo' } });
      showToast(texts.errors.photoRead, { error: true });
    }
  }, [showToast]);

  const onCancel = useCallback(() => {
    setFileToUpload(null);
    setPickedPhotoUrl('');
  }, []);

  const onUpload = useCallback(async () => {
    const { fileFormat, fileType } = CONVERTED_UPLOAD_FORMAT;
    const croppedImageUrl = getCroppedImageUrl(cropper, fileType, PHOTO_QUALITY, {
      maxWidth: PHOTO_SIZE_PX,
      maxHeight: PHOTO_SIZE_PX,
    });
    const storagePath = format(storagePaths.COACH_LEAD_END_SCREEN, {
      [pathPlaceholder.COACH_ID]: coachConfigDoc.id,
    });

    setIsUploading(true);
    try {
      const { state, publicUrl } = await uploadAttachment(
        croppedImageUrl,
        fileFormat,
        storagePath,
        getFileName(fileToUpload.name),
        fileType,
      );
      if (state === StorageProcessState.FAILED) {
        throw new Error(texts.errors.save);
      }
      await coachConfigDoc.updateLeadConfig({ [LeadConfigOptions.CONTACT_CARD_PHOTO_URL]: publicUrl }, userName);
      if (isComponentMountedRef.current) {
        setFileToUpload(null);
        setPickedPhotoUrl('');
        showToast(texts.contactPhotoSaved, { type: 'success' });
      }
    } catch (error) {
      Sentry.captureException(error, { extra: { message: 'Failed to upload a contact photo' } });
      if (isComponentMountedRef.current) {
        showToast(texts.errors.upload, { error: true });
      }
    } finally {
      if (isComponentMountedRef.current) {
        setIsUploading(false);
      }
    }
  }, [cropper, coachConfigDoc, fileToUpload, uploadAttachment, userName, showToast, isComponentMountedRef]);

  const onUseHeroImage = useCallback(async () => {
    try {
      await coachConfigDoc.updateLeadConfig({ [LeadConfigOptions.CONTACT_CARD_PHOTO_URL]: null }, userName);
      showToast(texts.heroImageRestored, { type: 'success' });
    } catch (error) {
      Sentry.captureException(error, { extra: { message: 'Failed to restore the contact photo' } });
      showToast(texts.errors.save, { error: true });
    }
  }, [coachConfigDoc, userName, showToast]);

  return (
    <>
      <InputLabel>{texts.contactCardPhoto}</InputLabel>
      <HeroImageContainer>
        <HeroImageUploader>
          {fileToUpload ? (
            <ImageCrop onCropperChange={setCropper} aspectRatio={1} imageUrl={previewUrl} />
          ) : (
            <ContactPhotoWrapper>
              <StyledFileSelectButton
                onSelect={onImageSelected}
                multiple={false}
                extraInputProps={{ accept: 'image/*', 'aria-label': texts.contactCardPhoto }}
                extraButtonProps={{ disabled: isReadOnly }}
              >
                {previewUrl ? (
                  <>
                    <StyledEditIcon><EditOutlined /></StyledEditIcon>
                    <ContactPhotoPreview src={previewUrl} alt={texts.contactCardPhoto} />
                  </>
                ) : <StyledImageSearch />}
              </StyledFileSelectButton>
            </ContactPhotoWrapper>
          )}
          <LoadingButton
            onClick={onUpload}
            disabled={isReadOnly || !fileToUpload || isUploading}
            isLoading={isUploading}
          >
            {customPhotoUrl ? texts.updateContactPhoto : texts.uploadContactPhoto}
          </LoadingButton>
          {!!fileToUpload && (
            <Button onClick={onCancel} variant={variants.SECONDARY}>{texts.cancel}</Button>
          )}
          {!!customPhotoUrl && !fileToUpload && !isReadOnly && (
            <LinkButton onClick={onUseHeroImage}>{texts.useHeroImage}</LinkButton>
          )}
        </HeroImageUploader>
        <HeroImageDescriptionContainer>
          <HeroImageDescription>{texts.contactPhotoDescription}</HeroImageDescription>
          <HeroImageTipsDescription>{texts.contactPhotoTips}</HeroImageTipsDescription>
          <ul>
            {texts.contactPhotoRequirements.map((text) => (
              <li key={text}>{format(text, { maxFileSize: MAX_FILE_SIZE_MB })}</li>
            ))}
          </ul>
        </HeroImageDescriptionContainer>
      </HeroImageContainer>
    </>
  );
};

ContactPhotoSetting.propTypes = {
  coachConfigDoc: PropTypes.object.isRequired,
  coachAvatarUrl: PropTypes.string,
  userName: PropTypes.string,
  isReadOnly: PropTypes.bool,
  onCropOpenChange: PropTypes.func,
};

ContactPhotoSetting.defaultProps = {
  coachAvatarUrl: '',
  userName: '',
  isReadOnly: false,
  onCropOpenChange: () => {},
};

export default observer(ContactPhotoSetting);
